import {AccountRecovery} from './AccountRecovery';
import { useState, type FormEvent } from "react";
import type { UserDTO } from "@algoquest/contracts";
import * as authClient from "../services/api/authClient";
import { ApiClientError } from "../services/api/httpClient";

/**
 * Phase 10 — the LOGIN step of §42's BOOT → LOGIN → SYNC → MAIN MENU
 * flow. Deliberately minimal: email/password plus a display name for
 * registration, no "forgot password" or social-login flow — nothing in
 * the Master Prompt calls for those yet, and §42 itself says "keep
 * authentication UX simple." `onAuthenticated` hands the resulting user
 * back to `App.tsx`, which owns the screen transition to `"sync"` — this
 * component never touches `appStore` directly, keeping it a pure
 * presentation + form-submission component.
 */
export interface LoginScreenProps {
  onAuthenticated: (user: UserDTO) => void;
  initialMode?: Mode;
}

type Mode = "login" | "register";

export function LoginScreen({ onAuthenticated, initialMode="login" }: LoginScreenProps) {
  const [recover,setRecover]=useState(false);
  const [showPassword,setShowPassword]=useState(false);
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [schoolName,setSchoolName]=useState("");
  const [className,setClassName]=useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result =
        mode === "login"
          ? await authClient.login({ email, password })
          : await authClient.register({ email, password, displayName, schoolName, className });
      onAuthenticated(result.user);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if(recover)return <AccountRecovery action="forgot" onClose={()=>setRecover(false)}/>;
  return (
    <div className="flex h-full w-full items-start overflow-y-auto justify-center aq-auth-screen py-6 text-slate-100">
      <form onSubmit={handleSubmit} className="w-full max-w-sm aq-auth-card p-8 shadow-xl">
        <a href="/" className="mb-5 inline-block text-sm text-sky-200">← Kembali ke beranda</a>
        <h1 className="mb-1 text-2xl font-bold">ALGOQUEST 3D</h1>
        <p className="mb-6 text-sm text-slate-400">
          {mode === "login" ? "Sign in to sync your progress." : "Create an account to save your progress."}
        </p>

        {mode === "register" && (
          <label className="mb-4 block text-sm">
            Display name
            <input
              type="text"
              required
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100"
            />
          </label>
        )}

        {mode === "register" && <>
          <label className="mb-4 block text-sm">School name<input required maxLength={160} value={schoolName} onChange={e=>setSchoolName(e.target.value)} placeholder="e.g. SMA Negeri 15 Bandung" className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-3 py-2"/></label>
          <label className="mb-4 block text-sm">Class<input required maxLength={80} value={className} onChange={e=>setClassName(e.target.value)} placeholder="e.g. X IPA 2" className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-3 py-2"/></label>
          <p className="mb-4 text-xs text-slate-400">Your school and class help administrators review your learning progress.</p>
        </>}

        <label className="mb-4 block text-sm">
          Email
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100"
          />
        </label>

        <label className="mb-2 block text-sm">
          Password
          <input
            id="student-password"
            type={showPassword?"text":"password"}
            autoComplete={mode==="register"?"new-password":"current-password"}
            required
            minLength={mode === "register" ? 10 : 1}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100"
          />
        </label>
        <button type="button" aria-controls="student-password" aria-pressed={showPassword} className="my-2 block text-sm text-sky-200" onClick={()=>setShowPassword(value=>!value)}>{showPassword?"Sembunyikan password":"Tampilkan password"}</button>
        {mode === "login" && <button type="button" className="my-3 text-sm text-sky-300" onClick={()=>setRecover(true)}>Lupa password?</button>}
        {mode === "register" && <p className="mb-4 text-xs text-slate-500">At least 10 characters.</p>}

        {error && <p className="mb-4 text-sm text-red-400">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded aq-auth-submit px-4 py-2 font-semibold disabled:opacity-50"
        >
          {submitting ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
        </button>

        <button
          type="button"
          onClick={() => {
            setMode(mode === "login" ? "register" : "login");
            setShowPassword(false);
            setError(null);
          }}
          className="mt-4 w-full text-center text-sm text-slate-400 hover:text-slate-200"
        >
          {mode === "login" ? "Need an account? Register" : "Already have an account? Sign in"}
        </button>
      </form>
    </div>
  );
}
